import React from 'react';
import Layout from '../components/Layout';
import {
  BuildingOffice2Icon,
  BoltIcon,
  LightBulbIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import broomIcon from '../assets/icons/broom.png';
import lawnMowerIcon from '../assets/icons/lawn-mower.png';
import founderPhoto from '../assets/screenshots/founder.jpg?url';
import SEO from '../components/SEO';
import { canonicalUrlFor, metaDescription } from '../utils/seo';

const About: React.FC = () => {
  const PngIcon: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
    <img
      src={src}
      alt={alt}
      className="h-8 w-8 object-contain"
      width={32}
      height={32}
      loading="lazy"
    />
  );

  const serviceCards = [
    { title: 'Construction and Contracting', icon: BuildingOffice2Icon },
    { title: 'Landscaping & Snow', imageSrc: lawnMowerIcon },
    { title: 'Electrical', icon: BoltIcon },
    { title: 'Janitorial & Facilities', imageSrc: broomIcon },
    { title: 'Professional Services', icon: LightBulbIcon },
    { title: 'Transportation', icon: TruckIcon },
  ];
  return (
    <Layout>
      <SEO
        title="About OppFoundry"
        description={metaDescription('OppFoundry helps service businesses discover public contract opportunities quickly. Learn why we built it and what comes next.')}
        canonical={canonicalUrlFor()}
      />
      {/* Intro */}
      <div className="space-y-4">
        <h1 className="text-3xl font-semibold text-gray-900">About OppFoundry</h1>
        <p className="text-base text-gray-600">
          OppFoundry exists to help service businesses grow and succeed by making it easier to find opportunities that you might not otherwise know are out there. Today we show opportunities that cities and state governmental agencies in Massachusetts are soliciting for, but our plan is to expand to commercial as well. And as you’ll see across the site, OppFoundry is brand new and feedback is eagerly desired so that it can continue to improve and be valuable to you. Please don’t hesitate to reach out.
        </p>
      </div>

      {/* Types of opportunities */}
      <div className="mt-10">
        <h2 className="text-xl font-semibold text-gray-900">Types of opportunities you'll find on OppFoundry</h2>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {serviceCards.map(({ title, icon: Icon, imageSrc }) => (
            <div
              key={title}
              className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-[0_10px_24px_rgba(167,139,250,0.32)]"
            >
              {imageSrc ? (
                <PngIcon src={imageSrc} alt={title} />
              ) : (
                Icon && <Icon className="h-6 w-6 text-blue-600" />
              )}
              <div>
                <p className="font-semibold text-gray-900">{title}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Feedback CTA (about only) */}
      <div id="contact" className="mt-12 rounded-2xl border border-blue-200 bg-blue-50 p-6">
        <h2 className="text-lg font-semibold text-blue-900">We want your feedback</h2>
        <p className="mt-2 text-sm text-blue-900/90">
          We’re building OppFoundry with service business owners. Tell us what would make this most valuable for you—features,
          data sources, alerts, anything.
        </p>
        <p className="mt-3 text-sm text-blue-900/90">
          Don’t have a public email yet? No problem—reach out once it’s ready and we’ll add it site‑wide.
        </p>
      </div>

      {/* Bio (about only) */}
      <div className="mt-12 grid gap-6 md:grid-cols-[160px_1fr] items-start">
        <img
          src={founderPhoto}
          alt="Photo of OppFoundry founder"
          className="h-40 w-40 rounded-xl object-cover ring-2 ring-blue-100"
          loading="lazy"
        />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Who’s behind OppFoundry</h3>
          <p className="mt-2 text-sm text-gray-700">
            My name is Ron Golan, and I’m the guy you see over there on the left. I built OppFoundry as a bit of an accident,
            having originally set out to start a landscaping business. It took me a bit to realize that this wasn’t a great idea,
            being from NYC and having never really even mowed a lawn. However in the course of planning that business, I realized
            that there’s a whole world of opportunities that too many service business owners don’t seem to even know about: the
            government and large scale commercial businesses solicit bids from vendors when they want to acquire services. That’s my
            goal with OppFoundry, to make it easier for service businesses to find these opportunities and grow your revenue. If you have feedback, feature requests, or just want to chat please don't hesitate to reach out directly to me at ron@oppfoundry.com.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default About;
